import React, { useCallback, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { JiraCascadingSelectField } from '@atlassian/jira-platform-fields/JiraCascadingSelectField.tsx';
import { JiraCheckboxesField } from '@atlassian/jira-platform-fields/JiraCheckboxesField.tsx';
import { JiraComponentsField } from '@atlassian/jira-platform-fields/JiraComponentsField.tsx';
import { JiraDatePickerField } from '@atlassian/jira-platform-fields/JiraDatePickerField.tsx';
import { JiraDateTimePickerField } from '@atlassian/jira-platform-fields/JiraDateTimePickerField.tsx';
import { JiraIssueLinkField } from '@atlassian/jira-platform-fields/JiraIssueLinkField.tsx';
import { JiraLabelsField } from '@atlassian/jira-platform-fields/JiraLabelsField.tsx';
import { JiraMultipleSelectField } from '@atlassian/jira-platform-fields/JiraMultipleSelectField.tsx';
import { JiraMultipleSelectUserPickerField } from '@atlassian/jira-platform-fields/JiraMultipleSelectUserPickerField.tsx';
import { JiraNumberField } from '@atlassian/jira-platform-fields/JiraNumberField.tsx';
import { JiraPeopleField } from '@atlassian/jira-platform-fields/JiraPeopleField.tsx';
import { JiraPriorityField } from '@atlassian/jira-platform-fields/JiraPriorityField.tsx';
import { JiraRadioSelectField } from '@atlassian/jira-platform-fields/JiraRadioSelectField.tsx';
import { JiraRichTextField } from '@atlassian/jira-platform-fields/JiraRichTextField.tsx';
import { JiraSingleLineTextField } from '@atlassian/jira-platform-fields/JiraSingleLineTextField.tsx';
import { JiraSingleSelectField } from '@atlassian/jira-platform-fields/JiraSingleSelectField.tsx';
import { JiraSingleSelectUserPickerField } from '@atlassian/jira-platform-fields/JiraSingleSelectUserPickerField.tsx';
import { JiraSprintField } from '@atlassian/jira-platform-fields/JiraSprintField.tsx';
import { JiraTimeTrackingField } from '@atlassian/jira-platform-fields/JiraTimeTrackingField.tsx';
import { JiraUrlField } from '@atlassian/jira-platform-fields/JiraUrlField.tsx';

import { isSystemField } from '../../../../../../common/utils/jira-create-with-rovo-preview-card';

import type { field_AtlassianConversationAssistantPreviewCard$key as FieldKey } from './__generated__/field_AtlassianConversationAssistantPreviewCard.graphql';

export const Field = ({
	as,
	field,
	fieldValue,
	invocationId,
	isFullWidth,
	isEditable,
	projectKey,
	onFieldChange,
	isStagingArea,
	sendAifcAnalyticsEvent,
}: {
	as?: 'heading' | 'default';
	field: FieldKey | null;
	fieldValue: unknown;
	invocationId: string;
	isEditable: boolean;
	isFullWidth?: boolean;
	projectKey: string;
	onFieldChange: (
		invocationId: string,
		fieldId: string,
		newValue: unknown,
		skipFireAnalytics?: boolean,
	) => void;
	isStagingArea: boolean;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): React.JSX.Element => {
	const fieldData = useFragment<FieldKey>(
		graphql`
			fragment field_AtlassianConversationAssistantPreviewCard on JiraIssueField {
				__typename
				fieldId
				... on JiraIssueFieldConfiguration {
					fieldConfig {
						isRequired
					}
				}
				...JiraSingleLineTextField_Platform @dangerously_unaliased_fixme
				...JiraRichTextField_Platform @dangerously_unaliased_fixme
				...JiraSingleSelectUserPickerField_Platform @dangerously_unaliased_fixme
				...JiraMultipleSelectUserPickerField_Platform @dangerously_unaliased_fixme
				...JiraPeopleField_Platform @dangerously_unaliased_fixme
				...JiraPriorityField_Platform @dangerously_unaliased_fixme
				...JiraSingleSelectField_Platform @dangerously_unaliased_fixme
				...JiraDatePickerField_Platform @dangerously_unaliased_fixme
				...JiraDateTimePickerField_Platform @dangerously_unaliased_fixme
				...JiraNumberField_Platform @dangerously_unaliased_fixme
				...JiraTimeTrackingField_Platform @dangerously_unaliased_fixme
				...JiraRadioSelectField_Platform @dangerously_unaliased_fixme
				...JiraMultipleSelectField @dangerously_unaliased_fixme
				...JiraLabelsField_Platform @dangerously_unaliased_fixme
				...JiraCascadingSelectField_Platform @dangerously_unaliased_fixme
				...JiraCheckboxesField_Platform @dangerously_unaliased_fixme
				...JiraUrlField_Platform @dangerously_unaliased_fixme
				...JiraComponentsField @dangerously_unaliased_fixme
				...JiraSprintField_Platform @dangerously_unaliased_fixme
			}
		`,
		field,
	);

	const fieldType = fieldData?.__typename;
	const isRequired = !!fieldData?.fieldConfig?.isRequired;

	const onChange = useCallback(
		(newValue: unknown, skipFireAnalytics = false) => {
			onFieldChange(invocationId, fieldData?.fieldId ?? '', newValue, skipFireAnalytics);
			if (!skipFireAnalytics) {
				sendAifcAnalyticsEvent?.({
					action: 'applied',
					actionSubject: 'inlineEdit',
					actionSubjectId: 'createWorkItemsInlineEdit',
					attributes: {
						// Only available in single work item preview card
						isSingleWorkItem: true,
						source: 'createWithRovoPreviewCard',
						fieldType,
						isRequired,
						isSystemField: isSystemField(fieldData?.fieldId),
						isStagingArea,
					},
				});
			}
		},
		[
			onFieldChange,
			invocationId,
			fieldData?.fieldId,
			sendAifcAnalyticsEvent,
			fieldType,
			isRequired,
			isStagingArea,
		],
	);

	// Checking common schema patterns for a value if the fieldValue is an object to
	// mitigate the rendering of [object Object] as the fieldValue
	const valueToRender = useMemo(() => {
		if (typeof fieldValue === 'object' && fieldValue !== null) {
			if (
				'user' in fieldValue &&
				typeof fieldValue.user === 'object' &&
				fieldValue.user !== null &&
				'name' in fieldValue.user
			) {
				return String(fieldValue.user.name);
			}
			if ('value' in fieldValue) {
				return String(fieldValue.value);
			}
			if ('label' in fieldValue) {
				return String(fieldValue.label);
			}
			if ('id' in fieldValue) {
				return String(fieldValue.id);
			}
		}
		return String(fieldValue);
	}, [fieldValue]);

	switch (fieldData?.__typename) {
		case 'JiraSingleLineTextField':
			return (
				<JiraSingleLineTextField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
					as={as}
					isFullWidth={isFullWidth}
				/>
			);
		case 'JiraRichTextField':
			return (
				<JiraRichTextField
					field={fieldData}
					fieldValue={fieldValue}
					isFieldEditable={isEditable}
					onChange={onChange}
					isFullWidth={isFullWidth}
				/>
			);
		case 'JiraSingleSelectUserPickerField':
			return (
				<JiraSingleSelectUserPickerField
					field={fieldData}
					fieldId={fieldData.fieldId}
					fieldValue={fieldValue}
					isFieldEditable={isEditable}
					onChange={onChange}
					projectKey={projectKey}
				/>
			);
		case 'JiraMultipleSelectUserPickerField':
			return (
				<JiraMultipleSelectUserPickerField
					field={fieldData}
					fieldId={fieldData.fieldId}
					fieldValue={fieldValue}
					isFieldEditable={isEditable}
					onChange={onChange}
					projectKey={projectKey}
				/>
			);
		case 'JiraPeopleField':
			return (
				<JiraPeopleField
					field={fieldData}
					fieldId={fieldData.fieldId}
					fieldValue={fieldValue}
					isFieldEditable={isEditable}
					onChange={onChange}
					projectKey={projectKey}
				/>
			);
		case 'JiraPriorityField':
			return (
				<JiraPriorityField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraSingleSelectField':
			return (
				<JiraSingleSelectField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraMultipleSelectField':
			return (
				<JiraMultipleSelectField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraDatePickerField':
			return (
				<JiraDatePickerField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraDateTimePickerField':
			return (
				<JiraDateTimePickerField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraNumberField':
			return (
				<JiraNumberField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraTimeTrackingField':
			return (
				<JiraTimeTrackingField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraRadioSelectField':
			return (
				<JiraRadioSelectField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraLabelsField':
			return <JiraLabelsField field={fieldData} fieldValue={fieldValue} onChange={onChange} />;
		case 'JiraCascadingSelectField':
			return (
				<JiraCascadingSelectField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraCheckboxesField':
			return (
				<JiraCheckboxesField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraUrlField':
			return (
				<JiraUrlField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraComponentsField':
			return (
				<JiraComponentsField
					field={fieldData}
					fieldValue={fieldValue}
					onChange={onChange}
					isFieldEditable={isEditable}
				/>
			);
		case 'JiraIssueLinkField':
			return <JiraIssueLinkField fieldValue={fieldValue} />;
		case 'JiraSprintField':
			if (fg('jira-aifc-editable-sprint-field')) {
				return (
					<JiraSprintField
						field={fieldData}
						fieldValue={fieldValue}
						onChange={onChange}
						isFieldEditable={isEditable}
					/>
				);
			}
			return (
				<Text size="medium">
					{FeatureGates.getExperimentValue(
						'jira-aifc-workflow-resumption-ui-updates',
						'isEnabled',
						false,
						{
							fireExperimentExposure: false,
						},
					)
						? valueToRender
						: String(fieldValue)}
				</Text>
			);
		default:
			return (
				<Text size="medium">
					{FeatureGates.getExperimentValue(
						'jira-aifc-workflow-resumption-ui-updates',
						'isEnabled',
						false,
						{
							fireExperimentExposure: false,
						},
					)
						? valueToRender
						: String(fieldValue)}
				</Text>
			);
	}
};
